import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { useToast, Spinner } from '../shared/UI';

export function MappingMatrix() {
  const [counters, setCounters] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const { show, ToastContainer } = useToast();

  const load = async () => {
    setLoading(true);
    const [c, s] = await Promise.all([
      api.get('/api/counters/?active=true'),
      api.get('/api/services/?tree=true&active=true&include_counters=true'),
    ]);
    setCounters(c);
    
    // Find leaves and store their full path
    const leaves = [];
    const findLeaves = (list, path = []) => {
      list.forEach(svc => {
        const currentPath = [...path, svc.name];
        if (!svc.children || svc.children.length === 0) {
          leaves.push({ ...svc, fullPath: currentPath.join(' > ') });
        } else {
          findLeaves(svc.children, currentPath);
        }
      });
    };
    findLeaves(s);
    setServices(leaves);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const isMapped = (counterId, service) => {
    return service.counter_ids?.includes(counterId);
  };

  const toggleMapping = async (counterId, serviceId, currentlyMapped) => {
    const key = `${counterId}-${serviceId}`;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await api.post('/api/counters/mapping/service', {
        counter_id: counterId, service_id: serviceId,
        action: currentlyMapped ? 'remove' : 'add'
      });
      show(currentlyMapped ? 'Unmapped' : 'Mapped', 'info');
      load();
    } catch (e) {
      show(e.error || 'Failed to update mapping', 'error');
    } finally {
      setSaving(s => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <ToastContainer />
      <div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: 'var(--navy)' }}>Mapping Matrix</h1>
        <p style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 14 }}>
          Assign leaf services to counters. Mapping is strictly enforced at the lowest level.
        </p>
      </div>

      <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ padding: '16px 20px', textAlign: 'left', fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', background: 'var(--navy)', color: '#fff', minWidth: 250, borderRadius: '12px 0 0 0' }}>
                Service Path
              </th>
              {counters.map((c, i) => (
                <th key={c.id} className="matrix-counter-header" style={{
                  padding: '16px 14px', textAlign: 'center', fontFamily: 'var(--font-display)',
                  fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                  background: 'var(--navy)', color: '#fff', minWidth: 110,
                  borderLeft: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: i === counters.length - 1 ? '0 12px 0 0' : 0,
                }}>
                  <div>{c.label}</div>
                  <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.7, marginTop: 2 }}>{c.name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {services.map((svc, ri) => (
              <tr key={svc.id} style={{ borderTop: '1px solid var(--gray-100)', background: ri % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                <td className="matrix-service-cell" style={{ padding: '14px 20px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--navy)', borderRight: '1px solid var(--gray-100)' }}>
                  <div style={{ fontWeight: 600 }}>{svc.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{svc.fullPath}</div>
                </td>
                {counters.map(c => {
                  const mapped = isMapped(c.id, svc);
                  const key = `${c.id}-${svc.id}`;
                  const isSaving = saving[key];
                  return (
                    <td key={c.id} className="matrix-counter-cell" style={{ padding: '10px 14px', textAlign: 'center', borderLeft: '1px solid var(--gray-100)' }}>
                      <button
                        onClick={() => toggleMapping(c.id, svc.id, mapped)}
                        disabled={isSaving}
                        style={{
                          width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
                          background: mapped ? 'var(--cobalt)' : 'var(--gray-100)',
                          color: mapped ? '#fff' : 'var(--gray-400)',
                          fontSize: 15, transition: 'all 0.15s', fontWeight: 700,
                          boxShadow: mapped ? '0 2px 8px rgba(21,101,192,0.3)' : 'none',
                        }}
                        title={mapped ? 'Click to unmap' : 'Click to map'}>
                        {isSaving ? '…' : mapped ? '✓' : '○'}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
